/**
 * RoleAccessCard — muestra los entitlements de un rol agrupados por
 * DigitalAsset (producto), usando los nombres reales del API.
 *
 * Requiere un `AssetCatalog` construido por `useAssetCatalog`.
 * Si el catálogo aún no cargó, muestra un fallback con el tagCode.
 */
import { Loader2, X } from 'lucide-react'
import type { KsecEntitlement } from '@/features/ksec/types'
import type { AssetCatalog } from '@/features/assets/useAssetCatalog'
import { cn } from '@/lib/utils'

// ─── Colores por producto (se asignan dinámicamente por orden de aparición) ───

const PRODUCT_COLORS = [
  { badge: 'bg-blue-50 text-blue-700 border-blue-200', dot: 'bg-blue-500' },
  { badge: 'bg-green-50 text-green-700 border-green-200', dot: 'bg-green-500' },
  { badge: 'bg-purple-50 text-purple-700 border-purple-200', dot: 'bg-purple-500' },
  { badge: 'bg-orange-50 text-orange-700 border-orange-200', dot: 'bg-orange-500' },
  { badge: 'bg-pink-50 text-pink-700 border-pink-200', dot: 'bg-pink-500' },
]

const FALLBACK_COLOR = { badge: 'bg-gray-50 text-gray-600 border-gray-200', dot: 'bg-gray-400' }

// ─── Configuración visual por nivel de acción ─────────────────────────────────

const ACTION_CONFIG: Record<string, { label: string; className: string }> = {
  ALL: { label: 'Acceso completo', className: 'bg-green-100 text-green-700' },
  RW:  { label: 'Leer y escribir', className: 'bg-blue-100 text-blue-700' },
  R:   { label: 'Solo lectura',    className: 'bg-gray-100 text-gray-600' },
  W:   { label: 'Solo escritura',  className: 'bg-yellow-100 text-yellow-700' },
  NONE:{ label: 'Denegado',        className: 'bg-red-100 text-red-600' },
}

// ─── Sub-componentes ──────────────────────────────────────────────────────────

interface ActionBadgeProps {
  action: string
  compact?: boolean
}

export function ActionBadge({ action, compact = false }: ActionBadgeProps) {
  const config = ACTION_CONFIG[action] ?? ACTION_CONFIG['R']
  return (
    <span className={cn('rounded px-2 py-0.5 text-xs font-medium', config.className)}>
      {compact ? action : config.label}
    </span>
  )
}

// ─── Componente principal ─────────────────────────────────────────────────────

interface RoleAccessCardProps {
  entitlements: KsecEntitlement[]
  /** Catálogo dinámico cargado por useAssetCatalog. */
  catalog: AssetCatalog
  loading?: boolean
  compact?: boolean
  emptyMessage?: string
  /** Si se provee, se muestra un botón X por entitlement para eliminarlo. */
  onDelete?: (ent: KsecEntitlement) => void
  /** ID del entitlement que se está eliminando (muestra spinner). */
  removingId?: string
}

/**
 * Agrupa los entitlements por DigitalAsset usando los nombres reales del API.
 * El orden de los colores sigue el orden en que aparecen los assets.
 */
export function RoleAccessCard({
  entitlements,
  catalog,
  loading = false,
  compact = false,
  emptyMessage = 'Este rol no tiene accesos configurados.',
  onDelete,
  removingId,
}: RoleAccessCardProps) {
  if (loading) {
    return (
      <div className="flex items-center gap-2 text-xs text-text-tertiary">
        <Loader2 className="h-3.5 w-3.5 animate-spin text-brand-500" />
        Cargando accesos…
      </div>
    )
  }

  if (entitlements.length === 0) {
    return <p className="text-xs text-text-tertiary">{emptyMessage}</p>
  }

  // Agrupar por assetName (o por tagCode prefix como fallback si el catálogo aún carga)
  const groups = new Map<string, { assetName: string; items: KsecEntitlement[] }>()

  for (const ent of entitlements) {
    const entry = ent.digitalAssetItemTagCode
      ? catalog.byTag.get(ent.digitalAssetItemTagCode)
      : undefined

    // Si el catálogo ya cargó → usar assetName real. Si no → usar el prefijo del tagCode.
    const groupKey = entry?.assetName
      ?? (ent.digitalAssetItemTagCode?.split('.')[0]?.toUpperCase() ?? 'DESCONOCIDO')

    if (!groups.has(groupKey)) {
      groups.set(groupKey, { assetName: groupKey, items: [] })
    }
    groups.get(groupKey)!.items.push(ent)
  }

  const groupEntries = [...groups.entries()]

  return (
    <div className={cn('space-y-3', compact && 'space-y-2')}>
      {groupEntries.map(([groupKey, { assetName, items }], idx) => {
        const color = PRODUCT_COLORS[idx % PRODUCT_COLORS.length] ?? FALLBACK_COLOR

        return (
          <div key={groupKey} className="space-y-1.5">
            {/* Header del producto — nombre real del DigitalAsset */}
            <div className="flex items-center gap-1.5">
              <span className={cn('h-2 w-2 shrink-0 rounded-full', color.dot)} />
              <span className={cn('rounded-full border px-2 py-0.5 text-xs font-semibold', color.badge)}>
                {assetName}
              </span>
            </div>

            {/* Items del producto */}
            <div className="ml-3.5 flex flex-col gap-1">
              {items.map((e) => {
                const entry = e.digitalAssetItemTagCode
                  ? catalog.byTag.get(e.digitalAssetItemTagCode)
                  : undefined

                // Nombre del ítem: real del API o tagCode limpio como fallback
                const itemLabel = entry?.itemName
                  ?? e.digitalAssetItemTagCode?.replace(/\./g, ' › ')
                  ?? '—'

                const isRemoving = removingId === e.id

                return (
                  <div key={e.id} className="flex items-center justify-between gap-3">
                    <span className="min-w-0 truncate text-xs text-text-secondary">{itemLabel}</span>
                    <div className="flex shrink-0 items-center gap-1.5">
                      <ActionBadge action={e.action} compact={compact} />
                      {onDelete && (
                        <button
                          type="button"
                          disabled={isRemoving}
                          onClick={() => onDelete(e)}
                          className="rounded p-0.5 text-text-tertiary hover:bg-red-50 hover:text-red-500 disabled:opacity-50 transition-colors"
                          aria-label="Eliminar acceso"
                        >
                          {isRemoving
                            ? <Loader2 size={11} className="animate-spin" />
                            : <X size={11} />
                          }
                        </button>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        )
      })}
    </div>
  )
}
